'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { useApiFetch } from '@/features/auth'
import {
  fetchMyInvestorCases,
  fetchMyInvestorProfile,
  updateMyInvestorProfile,
  updateMyNotificationPreferences,
  type UpdateMyInvestorProfileInput,
  type UpdateNotificationPreferencesInput,
} from '@/features/investors/api/investors.api'
import { AppError } from '@/lib/errors/appError'
import { queryKeys } from '@/lib/queryKeys'

function toastError(error: unknown) {
  toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
}

export function useMyInvestorProfile() {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.investorsResource.me,
    queryFn: () => fetchMyInvestorProfile(authedFetch),
  })
}

export function useMyInvestorCases() {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.investorsResource.myCases,
    queryFn: () => fetchMyInvestorCases(authedFetch),
  })
}

export function useUpdateMyInvestorProfile() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateMyInvestorProfileInput) =>
      updateMyInvestorProfile(authedFetch, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.investorsResource.me, updated)
      toast({ variant: 'success', title: 'Profile saved' })
    },
    onError: toastError,
  })
}

export function useUpdateMyNotificationPreferences() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateNotificationPreferencesInput) =>
      updateMyNotificationPreferences(authedFetch, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.investorsResource.me, updated)
      toast({ variant: 'success', title: 'Notification preferences saved' })
    },
    onError: toastError,
  })
}
